import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

type QueryParamsType = Record<string, string>;

/**
 * Хук для работы с query-параметрами URL
 * @returns [queryParams, setQueryParams, clearQueryParams]
 */
export const useQueryParams = (): [
  URLSearchParams,
  (params: QueryParamsType, replace?: boolean) => void,
  () => void
] => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  
  // Получаем текущие параметры URL
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  
  // Функция для обновления параметров URL
  const setQueryParams = useCallback(
    (params: QueryParamsType, replace: boolean = false) => {
      const newParams = new URLSearchParams(queryParams);
      
      // Обновляем параметры
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
      
      // Формируем новый URL
      const newSearch = newParams.toString();
      const query = newSearch ? `?${newSearch}` : '';
      
      // Обновляем URL
      navigate(`${pathname}${query}`, { replace });
    },
    [navigate, pathname, queryParams]
  );
  
  // Функция для очистки всех параметров URL
  const clearQueryParams = useCallback(() => {
    navigate(pathname, { replace: true });
  }, [navigate, pathname]);
  
  return [queryParams, setQueryParams, clearQueryParams];
}; 